import { functions } from '@app/firebase';
import UserContext from '@context/UserContext';
import Sentry from '@integrations/Sentry';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Homes from '@pages/homes/Homes';
import useCommonStyles from '@styles/common.style';
import { useQuery } from '@tanstack/react-query';
import { httpsCallable } from 'firebase/functions';
import React, { useContext, useMemo } from 'react';

import QuoteCard from '../../components/quotes/QuoteCard';
import QuoteCardSkeleton from '../../components/quotes/QuoteCard.skeleton';

const getDealsByEmail = httpsCallable<{ email: string }, { deals: Array<{ deal: HubSpot.Deal; lineItems: HubSpot.LineItem[] }> }>(functions, 'http-getDealsByEmail');

const getTimeOfDay = (): string => {
  const hour = new Date().getHours();

  if (hour < 12) {
    return 'Morning';
  } else if (hour < 17) {
    return 'Afternoon';
  } else {
    return 'Evening';
  }
};


const Dashboard = () => {
  const userContext = useContext<UserContext>(UserContext);
  const { classes: common } = useCommonStyles();

  const timeOfDay = useMemo(() => getTimeOfDay(), []);
  const firstName = userContext?.data?.firstname || '';

  const { data: allDealsData = [], isLoading: loading } = useQuery({
    queryKey: ['dealsByEmail', userContext?.user?.email],
    queryFn: async () => {
      if (!userContext?.user?.email) {
        return [];
      }

      try {
        const result = await getDealsByEmail({ email: userContext.user.email });

        return result.data.deals || [];
      } catch (error) {
        Sentry.captureException(error);
        console.error('Failed to fetch deals:', error);
        throw error;
      }
    },
    enabled: !!userContext?.user?.email,
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for 5 minutes
  });

  // Take only the first 3 most recent deals for dashboard
  const dealsData = allDealsData.slice(0, 3);

  return (
    <>
      <Box
        className={common.contentSpacing}
      >
        <Typography
          component="h1"
          variant="h2"
          style={{
            fontSize: 48,
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          Good 
          {' '}
          {timeOfDay}
          {firstName ? `, ${firstName}` : ''}
        </Typography>
        <Typography
          variant="body1"
          style={{ fontSize: 24 }}
        >
          Here&apos;s what&apos;s happening with your listings today 🏡
        </Typography>

      </Box>
      <Box className={common.contentSpacing}>
        
        <Grid container>
          <Grid
            item
            xs={12}
            sm={6}
            style={{ marginBottom: 24 }}
          >
            <Typography
              component="h2"
              variant="h3Alt"
            >
              Recent quotes
            </Typography>
            <Typography
              variant="body1"
              style={{ fontSize: 18 }}
            >
              Manage your quotes, make a payment and easily share with others to get on the market faster
            </Typography>
          </Grid>
        </Grid>

        {loading ? (
          <Grid
            container
            spacing={2}
          >
            {[...Array(3)].map((_, index) => (
              <Grid
                key={index}
                item
                xs={12}
                md={6}
                lg={4}
                sx={{ display: 'flex', width: '100%' }}
              >
                <QuoteCardSkeleton />
              </Grid>
            ))}
          </Grid>
        ) : dealsData.length ? (
          <Grid
            container
            spacing={2}
          >
            {dealsData.map((dealData) => {
              const deal = dealData.deal;

              if (!deal.id) {
                return null;
              }
              const id = deal.id;
              const address = deal.properties?.staging_address || deal.properties?.dealname || 'Quote';
              const created = deal.properties?.hs_createdate;
              const installDate = deal.properties?.staging_date;

              return (
                <Grid
                  key={id}
                  item
                  xs={12}
                  md={6}
                  lg={4}
                  sx={{ display: 'flex', width: '100%' }}
                >
                  <QuoteCard
                    id={id}
                    address={address}
                    created={created}
                    installDate={installDate}
                    lineItems={dealData.lineItems}
                  />
                </Grid>
              );
            })}
          </Grid>
        ) : (
          <Typography
            variant="body2"
            style={{ opacity: 0.8 }}
          >
            No quotes yet.
          </Typography>
        )}
      </Box>
      <Homes simple />
    </>
  );
};

export default React.memo(Dashboard);
